import { useEffect, useState, type FormEvent } from 'react';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useAuthStore } from '@/stores/authStore';

interface TenantSettings {
  proposalValueApprovalThreshold: number | null;
  proposalDiscountApprovalThreshold: number | null;
  staleOpportunityDays: number;
  costVisibilityLevel: string;
  agentRuntimeEnabled: boolean;
  smartLookupEnabled: boolean;
  attachmentStorageCapBytes: number | null;
  deleteApprovalManagerThreshold: number;
  deleteApprovalCurrency: string;
  sessionIdleTimeoutMinutes: number;
  maintenanceBannerEnabled: boolean;
  maintenanceBannerMessage: string | null;
  maintenanceBannerReturnAt: string | null;
  maintenanceBannerSupportUrl: string | null;
  maintenanceBannerSupportLabel: string | null;
  maintenanceBannerSeverity: 'INFO' | 'WARNING' | 'CRITICAL';
}

interface AttachmentUsage {
  usedBytes: number;
  capBytes: number | null;
}

const VISIBILITY_OPTIONS = ['ALL', 'MANAGER_AND_ABOVE', 'OWNER_ONLY'] as const;

const VISIBILITY_LABELS: Record<string, string> = {
  ALL: 'All users',
  MANAGER_AND_ABOVE: 'Manager and above',
  OWNER_ONLY: 'Owner only',
};

export function TenantSettingsPage() {
  const role = useAuthStore((s) => s.user?.role);
  const [settings, setSettings] = useState<TenantSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  // Form fields
  const [valueThreshold, setValueThreshold] = useState('');
  const [discountThreshold, setDiscountThreshold] = useState('');
  const [staleDays, setStaleDays] = useState('');
  const [costVisibility, setCostVisibility] = useState('MANAGER_AND_ABOVE');
  const [agentEnabled, setAgentEnabled] = useState(false);
  const [smartLookupEnabled, setSmartLookupEnabled] = useState(true);
  const [deleteThreshold, setDeleteThreshold] = useState('100000');
  const [deleteCurrency, setDeleteCurrency] = useState('USD');
  const [usage, setUsage] = useState<AttachmentUsage | null>(null);
  const [idleTimeout, setIdleTimeout] = useState<string>('60');
  const [maintEnabled, setMaintEnabled] = useState(false);
  const [maintMessage, setMaintMessage] = useState('');
  const [maintReturnAt, setMaintReturnAt] = useState('');
  const [maintSupportUrl, setMaintSupportUrl] = useState('');
  const [maintSupportLabel, setMaintSupportLabel] = useState('');
  const [maintSeverity, setMaintSeverity] = useState<'INFO' | 'WARNING' | 'CRITICAL'>('INFO');

  useEffect(() => {
    apiClient
      .get<TenantSettings>('/tenant-settings')
      .then(({ data }) => {
        setSettings(data);
        setValueThreshold(
          data.proposalValueApprovalThreshold?.toString() ?? '',
        );
        setDiscountThreshold(
          data.proposalDiscountApprovalThreshold?.toString() ?? '',
        );
        setStaleDays(data.staleOpportunityDays.toString());
        setCostVisibility(data.costVisibilityLevel);
        setAgentEnabled(data.agentRuntimeEnabled);
        setSmartLookupEnabled(data.smartLookupEnabled);
        setDeleteThreshold(data.deleteApprovalManagerThreshold.toString());
        setDeleteCurrency(data.deleteApprovalCurrency);
        setIdleTimeout((data.sessionIdleTimeoutMinutes ?? 60).toString());
        setMaintEnabled(data.maintenanceBannerEnabled ?? false);
        setMaintMessage(data.maintenanceBannerMessage ?? '');
        setMaintReturnAt(
          data.maintenanceBannerReturnAt
            ? toLocalInputValue(data.maintenanceBannerReturnAt)
            : '',
        );
        setMaintSupportUrl(data.maintenanceBannerSupportUrl ?? '');
        setMaintSupportLabel(data.maintenanceBannerSupportLabel ?? '');
        setMaintSeverity((data.maintenanceBannerSeverity as typeof maintSeverity) ?? 'INFO');
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    apiClient
      .get<AttachmentUsage>('/attachments/usage')
      .then(({ data }) => setUsage(data))
      .catch(() => setUsage(null));
  }, []);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(timer);
  }, [toast]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      const payload: TenantSettings = {
        proposalValueApprovalThreshold: valueThreshold.trim()
          ? parseFloat(valueThreshold)
          : null,
        proposalDiscountApprovalThreshold: discountThreshold.trim()
          ? parseFloat(discountThreshold)
          : null,
        staleOpportunityDays: parseInt(staleDays) || 14,
        costVisibilityLevel: costVisibility,
        agentRuntimeEnabled: agentEnabled,
        smartLookupEnabled,
        attachmentStorageCapBytes: settings?.attachmentStorageCapBytes ?? null,
        deleteApprovalManagerThreshold: parseFloat(deleteThreshold) || 100000,
        deleteApprovalCurrency: deleteCurrency,
        sessionIdleTimeoutMinutes: Math.max(0, parseInt(idleTimeout, 10) || 0),
        maintenanceBannerEnabled: maintEnabled,
        maintenanceBannerMessage: maintMessage.trim() || null,
        maintenanceBannerReturnAt: maintReturnAt
          ? new Date(maintReturnAt).toISOString()
          : null,
        maintenanceBannerSupportUrl: maintSupportUrl.trim() || null,
        maintenanceBannerSupportLabel: maintSupportLabel.trim() || null,
        maintenanceBannerSeverity: maintSeverity,
      };
      const { data } = await apiClient.patch<TenantSettings>(
        '/tenant-settings',
        payload,
      );
      setSettings(data);
      setToast('Settings saved successfully');
    } catch {
      setToast('Failed to save settings');
    } finally {
      setSaving(false);
    }
  }

  if (role && role !== 'TENANT_ADMIN' && role !== 'PLATFORM_ADMIN') {
    return <p className="text-sm text-destructive">Access denied</p>;
  }

  if (loading) {
    return <p className="text-sm text-muted-foreground">Loading...</p>;
  }

  if (!settings) {
    return (
      <p className="text-sm text-destructive">Failed to load settings</p>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">Tenant Settings</h1>

      <form onSubmit={handleSubmit} className="max-w-lg space-y-6">
        {/* Approval Thresholds */}
        <section className="space-y-4 rounded-lg border bg-card p-6">
          <h2 className="text-base font-medium">Approval Thresholds</h2>
          <div className="space-y-1">
            <label className="text-sm font-medium">
              Proposal Value Threshold
            </label>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={valueThreshold}
              onChange={(e) => setValueThreshold(e.target.value)}
              placeholder="e.g. 50000"
            />
            <p className="text-xs text-muted-foreground">
              Proposals above this value require approval
            </p>
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">
              Proposal Discount Threshold (%)
            </label>
            <Input
              type="number"
              step="1"
              min="0"
              max="100"
              value={discountThreshold}
              onChange={(e) => setDiscountThreshold(e.target.value)}
              placeholder="e.g. 15"
            />
            <p className="text-xs text-muted-foreground">
              Discounts above this percentage require approval (0-100)
            </p>
          </div>
        </section>

        {/* Pipeline Settings */}
        <section className="space-y-4 rounded-lg border bg-card p-6">
          <h2 className="text-base font-medium">Pipeline Settings</h2>
          <div className="space-y-1">
            <label className="text-sm font-medium">
              Stale Opportunity Days
            </label>
            <Input
              type="number"
              step="1"
              min="1"
              value={staleDays}
              onChange={(e) => setStaleDays(e.target.value)}
              placeholder="14"
            />
            <p className="text-xs text-muted-foreground">
              Opportunities without activity for this many days are flagged as
              stale
            </p>
          </div>
        </section>

        {/* Visibility */}
        <section className="space-y-4 rounded-lg border bg-card p-6">
          <h2 className="text-base font-medium">Visibility</h2>
          <div className="space-y-1">
            <label className="text-sm font-medium">
              Cost Visibility Level
            </label>
            <select
              value={costVisibility}
              onChange={(e) => setCostVisibility(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              {VISIBILITY_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {VISIBILITY_LABELS[opt]}
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground">
              Controls who can view cost and margin data
            </p>
          </div>
        </section>

        {/* Agent Runtime */}
        <section className="space-y-4 rounded-lg border bg-card p-6">
          <h2 className="text-base font-medium">Agent Runtime</h2>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setAgentEnabled(!agentEnabled)}
              className={`relative h-6 w-11 rounded-full transition-colors ${
                agentEnabled ? 'bg-primary' : 'bg-muted'
              }`}
            >
              <span
                className={`absolute top-0.5 h-5 w-5 rounded-full bg-card shadow transition-transform ${
                  agentEnabled ? 'translate-x-5' : 'translate-x-0.5'
                }`}
              />
            </button>
            <label className="text-sm font-medium">
              Agent Runtime Enabled
            </label>
          </div>
          <p className="text-xs text-muted-foreground">
            When enabled, AI agents will analyze your pipeline and generate
            recommendations
          </p>
        </section>

        {/* Delete approval threshold (Epic 86 / ADR-008) */}
        <section className="space-y-4 rounded-lg border bg-card p-6">
          <h2 className="text-base font-medium">Delete approval threshold</h2>
          <p className="text-xs text-muted-foreground">
            Opportunities with an estimated value below this threshold can
            be approved by a sales manager. Above the threshold, deletion
            requires a tenant admin. Leads always require manager approval.
          </p>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2 space-y-1">
              <label className="text-sm font-medium">Threshold amount</label>
              <Input
                type="number"
                step="1000"
                min="0"
                value={deleteThreshold}
                onChange={(e) => setDeleteThreshold(e.target.value)}
                placeholder="100000"
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Currency</label>
              <select
                value={deleteCurrency}
                onChange={(e) => setDeleteCurrency(e.target.value.toUpperCase())}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                {['USD', 'EUR', 'GBP', 'BRL', 'COP', 'MXN'].map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </section>

        {/* Attachment storage usage (Epic 88) */}
        {usage && (
          <section className="space-y-3 rounded-lg border bg-card p-6">
            <h2 className="text-base font-medium">Attachment storage</h2>
            {usage.capBytes === null ? (
              <p className="text-sm text-muted-foreground">
                <span className="font-medium">{(usage.usedBytes / 1024 / 1024).toFixed(1)} MB</span> used.
                No cap on this tenant (uncapped plan).
              </p>
            ) : (
              <>
                <p className="text-sm">
                  <span className="font-medium">
                    {(usage.usedBytes / 1024 / 1024).toFixed(1)} MB
                  </span>{' '}
                  of {(usage.capBytes / 1024 / 1024 / 1024).toFixed(1)} GB used
                  ({((usage.usedBytes / usage.capBytes) * 100).toFixed(1)}%)
                </p>
                <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                  <div
                    className={
                      usage.usedBytes / usage.capBytes > 0.8
                        ? 'h-full bg-amber-500'
                        : 'h-full bg-primary'
                    }
                    style={{
                      width: `${Math.min(100, (usage.usedBytes / usage.capBytes) * 100)}%`,
                    }}
                  />
                </div>
                {usage.usedBytes / usage.capBytes > 0.8 && (
                  <p className="text-xs text-amber-600">
                    You're past 80% of your cap. Contact platform admin to
                    upgrade the plan or remove unused attachments.
                  </p>
                )}
              </>
            )}
          </section>
        )}

        {/* Smart Lookup (Epic 89) */}
        <section className="space-y-4 rounded-lg border bg-card p-6">
          <h2 className="text-base font-medium">Smart Field Lookup</h2>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setSmartLookupEnabled(!smartLookupEnabled)}
              className={`relative h-6 w-11 rounded-full transition-colors ${
                smartLookupEnabled ? 'bg-primary' : 'bg-muted'
              }`}
            >
              <span
                className={`absolute top-0.5 h-5 w-5 rounded-full bg-card shadow transition-transform ${
                  smartLookupEnabled ? 'translate-x-5' : 'translate-x-0.5'
                }`}
              />
            </button>
            <label className="text-sm font-medium">
              Smart Lookup Enabled
            </label>
          </div>
          <p className="text-xs text-muted-foreground">
            When enabled, the ✨ button next to fields like Website and
            LinkedIn URL asks an AI to suggest a value from context. Each
            invocation costs 1 credit (cached for 30 days). Disable for
            data-residency-sensitive deployments.
          </p>
        </section>

        {/* Session idle timeout (default; users can override) */}
        <section className="space-y-3 rounded-lg border bg-card p-6">
          <h2 className="text-base font-medium">Session Idle Timeout</h2>
          <div className="space-y-1">
            <label className="text-sm font-medium">
              Default minutes of inactivity before logout
            </label>
            <Input
              type="number"
              min="0"
              max="1440"
              step="5"
              value={idleTimeout}
              onChange={(e) => setIdleTimeout(e.target.value)}
              placeholder="60"
            />
            <p className="text-xs text-muted-foreground">
              Tenant default applied to every user. Each user can override
              this for their own browser under <strong>Profile → Session</strong>.
              {' '}Set to <code>0</code> to disable idle logout entirely (only
              the JWT expiration limits the session). Max <code>1440</code>{' '}
              (24h).
            </p>
          </div>
        </section>

        {/* Maintenance banner (Story 18.7) */}
        <section className="space-y-4 rounded-lg border bg-card p-6">
          <h2 className="text-base font-medium">Maintenance banner</h2>
          <p className="text-xs text-muted-foreground">
            Show a banner at the top of the app to inform users about
            scheduled maintenance, an active incident, or a planned downtime
            window. Users can dismiss the banner per browser; the dismiss
            resets when the message changes.
          </p>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setMaintEnabled(!maintEnabled)}
              className={`relative h-6 w-11 rounded-full transition-colors ${
                maintEnabled ? 'bg-primary' : 'bg-muted'
              }`}
            >
              <span
                className={`absolute top-0.5 h-5 w-5 rounded-full bg-card shadow transition-transform ${
                  maintEnabled ? 'translate-x-5' : 'translate-x-0.5'
                }`}
              />
            </button>
            <label className="text-sm font-medium">Show banner to all users</label>
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">Severity</label>
            <select
              value={maintSeverity}
              onChange={(e) =>
                setMaintSeverity(e.target.value as typeof maintSeverity)
              }
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="INFO">Info (blue)</option>
              <option value="WARNING">Warning (amber)</option>
              <option value="CRITICAL">Critical (red)</option>
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">Message</label>
            <textarea
              rows={2}
              value={maintMessage}
              onChange={(e) => setMaintMessage(e.target.value)}
              placeholder="Scheduled maintenance — saving may be unavailable for a few minutes."
              className="w-full rounded-md border bg-background p-2 text-sm"
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">Expected return time (local)</label>
            <Input
              type="datetime-local"
              value={maintReturnAt}
              onChange={(e) => setMaintReturnAt(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Optional. Stored as an absolute timestamp; rendered in each
              user's locale and timezone.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-sm font-medium">Support URL</label>
              <Input
                type="url"
                value={maintSupportUrl}
                onChange={(e) => setMaintSupportUrl(e.target.value)}
                placeholder="mailto:support@example.com"
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Support link label</label>
              <Input
                value={maintSupportLabel}
                onChange={(e) => setMaintSupportLabel(e.target.value)}
                placeholder="Contact support"
              />
            </div>
          </div>
        </section>

        {/* Save */}
        <div className="flex justify-end">
          <Button type="submit" size="sm" disabled={saving}>
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </form>

      {toast && (
        <div className="fixed bottom-4 right-4 z-50 rounded-lg border bg-card px-4 py-3 text-sm shadow-lg">
          {toast}
        </div>
      )}
    </div>
  );
}

function toLocalInputValue(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}
